// src/pages/MeetingRooms.js
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { FiArrowUp, FiMapPin } from "react-icons/fi";

import RoomCards from "../components/meetingRooms/RoomCards";
import AvailableTimeSlots from "../components/meetingRooms/AvailableTimeSlots";
import BookingForm from "../components/meetingRooms/BookingForm";
import ScheduleTable from "../components/meetingRooms/ScheduleTable";
import WeeklyBookingCalendar from "../components/meetingRooms/WeeklyBookingCalendar";
import LoadingOverlay from "../components/LoadingOverlay";

import {
  todayKey,
  isOverlap,
  timeToMin,
  formatDateDMY,
} from "../components/meetingRooms/timeUtils";
import {
  fetchRooms,
  fetchTodayAvailability,
  fetchMyBookings,
  createBooking,
  updateBooking,
  deleteBooking,
  cacheRooms,
  getCachedRooms,
} from "../services/meetingRoomApi";

import Swal from "sweetalert2";
import { toast } from "react-toastify";

const MeetingRooms = () => {
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState("");
  const [date, setDate] = useState(todayKey());
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("11:00");
  const [purpose, setPurpose] = useState("");

  const [weeklyBookings, setWeeklyBookings] = useState([]); // ✅ ของทุกคนในสัปดาห์ที่แสดง
  const [myBookings, setMyBookings] = useState([]); // ✅ ของฉัน
  const [todayAvailability, setTodayAvailability] = useState(null);

  const [loadingRooms, setLoadingRooms] = useState(true);
  const [loadingMyBookings, setLoadingMyBookings] = useState(false);
  const [loadingWeeklyBookings, setLoadingWeeklyBookings] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // ✅ โหมดแก้ไข
  const [editingBooking, setEditingBooking] = useState(null); // booking object หรือ null

  // ✅ AUTO REFRESH: refs กันยิงซ้อน + เก็บ interval id
  const pollingRef = useRef(null);
  const isPollingRef = useRef(false);
  const bookingFormRef = useRef(null);

  const isDark = () => document.documentElement.classList.contains("dark");

  const swalBase = () => ({
    background: isDark() ? "#020617" : "#ffffff",
    color: isDark() ? "#E5E7EB" : "#111827",
    confirmButtonColor: "#6366F1",
  });

  const refreshTodayAvailability = useCallback(async () => {
    try {
      setTodayAvailability(await fetchTodayAvailability());
    } catch (error) {
      console.error("Load today's room availability error:", error);
    }
  }, []);

  // 1) โหลด rooms ตอนเปิดหน้า
  useEffect(() => {
    const loadRooms = async () => {
      const cachedRooms = getCachedRooms();
      if (cachedRooms?.length > 0) {
        setRooms(cachedRooms);
        setSelectedRoomId((prev) => prev || cachedRooms[0]._id);
        setLoadingRooms(false);
        return;
      }

      try {
        setLoadingRooms(true);
        const data = await fetchRooms();
        setRooms(data);
        cacheRooms(data);

        if (data?.length > 0) {
          setSelectedRoomId((prev) => prev || data[0]._id);
        }
      } catch (err) {
        console.error(err);
        toast.error("โหลดห้องประชุมไม่สำเร็จ");
      } finally {
        setLoadingRooms(false);
      }
    };

    loadRooms();
  }, []);

  useEffect(() => {
    refreshTodayAvailability();
  }, [refreshTodayAvailability]);

  const selectedRoom = useMemo(
    () => rooms.find((r) => r._id === selectedRoomId),
    [rooms, selectedRoomId],
  );

  useEffect(() => {
    setWeeklyBookings([]);
  }, [selectedRoomId]);

  // 2) โหลดเฉพาะรายการของฉัน; รายการทั้งหมดมาจากปฏิทินรายสัปดาห์
  useEffect(() => {
    if (!selectedRoomId || !date) return;

    const loadMyBookings = async () => {
      try {
        setLoadingMyBookings(true);
        setMyBookings(await fetchMyBookings({ roomId: selectedRoomId, dateKey: date }));
      } catch (err) {
        console.error(err);
        toast.error("โหลดรายการจองไม่สำเร็จ");
      } finally {
        setLoadingMyBookings(false);
      }
    };

    loadMyBookings();
  }, [selectedRoomId, date]);

  const dayBookings = useMemo(() => {
    return (weeklyBookings || [])
      .filter((booking) => (booking?.dateKey || booking?.bookingDate) === date)
      .slice()
      .sort(
        (a, b) =>
          (a.startMin ?? timeToMin(a.startTime)) -
          (b.startMin ?? timeToMin(b.startTime)),
      );
  }, [date, weeklyBookings]);

  // helper: refresh รายการของฉัน; ตารางรายวันรับข้อมูลจากปฏิทินรายสัปดาห์
  const refreshMyBookings = useCallback(async () => {
    if (!selectedRoomId || !date) return;

    setMyBookings(await fetchMyBookings({ roomId: selectedRoomId, dateKey: date }));
  }, [selectedRoomId, date]);

  // ✅ AUTO REFRESH ทุก 60 วินาที (ตอนอยู่หน้านี้)
  useEffect(() => {
    if (!selectedRoomId || !date) return;

    const poll = async () => {
      if (isPollingRef.current) return;
      if (document.visibilityState !== "visible") return;
      if (editingBooking) return;

      try {
        isPollingRef.current = true;
        await Promise.all([
          refreshMyBookings(),
          refreshTodayAvailability(),
        ]);
      } catch (e) {
        console.error("Auto refresh bookings error:", e);
      } finally {
        isPollingRef.current = false;
      }
    };

    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(poll, 60_000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  }, [
    selectedRoomId,
    date,
    editingBooking,
    refreshMyBookings,
    refreshTodayAvailability,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 320);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ✅ (ออปชัน) กลับมา tab แล้ว refresh ทันที
  useEffect(() => {
    if (!selectedRoomId || !date) return;

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        if (editingBooking) return;
        refreshMyBookings().catch((e) =>
          console.error("Visibility refresh error:", e),
        );
        refreshTodayAvailability();
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [
    selectedRoomId,
    date,
    editingBooking,
    refreshMyBookings,
    refreshTodayAvailability,
  ]);

  // ✅ ยกเลิกโหมดแก้ไข + เคลียร์ฟอร์ม
  const resetForm = () => {
    setEditingBooking(null);
    setPurpose("");
    setStartTime("10:00");
    setEndTime("11:00");
  };

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // ✅ validate ต้องเช็คชนกับ "ทุกคน" แต่ตอนแก้ไขต้อง "ยกเว้นของตัวเอง"
  const validate = () => {
    if (!selectedRoomId) return "กรุณาเลือกห้องประชุม";
    if (!date) return "กรุณาเลือกวันที่";
    if (!startTime || !endTime) return "กรุณาเลือกเวลาให้ครบ";
    if (endTime <= startTime) return "เวลาสิ้นสุดต้องมากกว่าเวลาเริ่ม";
    if (loadingWeeklyBookings) return "กำลังโหลดตารางการจอง กรุณารอสักครู่";

    const s = timeToMin(startTime);
    const e = timeToMin(endTime);

    const editingId = editingBooking?._id || editingBooking?.id || null;

    const conflict = dayBookings.some((b) => {
      const bId = b?._id || b?.id;
      if (editingId && String(bId) === String(editingId)) return false; // ✅ ข้ามตัวเองตอนแก้ไข

      return isOverlap(
        s,
        e,
        b.startMin ?? timeToMin(b.startTime),
        b.endMin ?? timeToMin(b.endTime),
      );
    });

    if (conflict) return "ช่วงเวลานี้มีคนจองแล้ว";

    return null;
  };

  // 3) create / update booking
  const onSubmit = async (e) => {
    e.preventDefault();

    const err = validate();
    if (err) {
      toast.warning(err);
      return;
    }

    const isEdit = !!editingBooking;

    const payload = {
      roomId: selectedRoomId,
      dateKey: date,
      startTime,
      endTime,
      purpose: purpose?.trim() || "-", // backend ยังใช้ purpose
    };

    const confirm = await Swal.fire({
      ...swalBase(),
      icon: "question",
      title: isEdit ? "ยืนยันการแก้ไขการจอง?" : "ยืนยันการจองห้องประชุม?",
      html: `
        <div style="text-align:left; line-height:1.6">
          <div><b>ห้อง:</b> ${selectedRoom?.name || "-"}</div>
          <div><b>วันที่:</b> ${formatDateDMY(date)}</div>
          <div><b>เวลา:</b> ${startTime} - ${endTime}</div>
          <div><b>หัวข้อการประชุม:</b> ${payload.purpose}</div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: isEdit ? "ยืนยันแก้ไข" : "ยืนยันจอง",
      cancelButtonText: "ยกเลิก",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;

    try {
      const loadingToastId = toast.loading(
        isEdit ? "กำลังบันทึกการแก้ไข..." : "กำลังบันทึกการจอง...",
      );

      if (isEdit) {
        await updateBooking(editingBooking._id, payload);
      } else {
        await createBooking(payload);
      }

      await refreshMyBookings();
      await refreshTodayAvailability();
      setCalendarRefreshKey((current) => current + 1);
      resetForm();

      toast.update(loadingToastId, {
        render: isEdit ? "แก้ไขการจองสำเร็จ ✅" : "จองห้องประชุมสำเร็จ ✅",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });

      await Swal.fire({
        ...swalBase(),
        icon: "success",
        title: isEdit ? "แก้ไขสำเร็จ" : "จองสำเร็จ",
        html: `
          <div style="text-align:left; line-height:1.6">
            <div><b>ห้อง:</b> ${selectedRoom?.name || "-"}</div>
            <div><b>วันที่:</b> ${formatDateDMY(date)}</div>
            <div><b>เวลา:</b> ${startTime} - ${endTime}</div>
            <div><b>หัวข้อการประชุม:</b> ${payload.purpose}</div>
          </div>
        `,
        confirmButtonText: "เรียบร้อย",
      });
    } catch (err2) {
      console.error(err2);
      const msg = err2?.response?.data?.message;

      if (err2?.response?.status === 409) {
        Swal.fire({
          ...swalBase(),
          icon: "error",
          title: "ช่วงเวลานี้ถูกจองแล้ว",
          text: msg || "กรุณาเลือกเวลาอื่น",
          confirmButtonText: "รับทราบ",
          confirmButtonColor: "#EF4444",
        });
        return;
      }

      Swal.fire({
        ...swalBase(),
        icon: "error",
        title: editingBooking
          ? "บันทึกการแก้ไขไม่สำเร็จ"
          : "บันทึกการจองไม่สำเร็จ",
        text: msg || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง",
        confirmButtonText: "ปิด",
      });
    }
  };

  const pageLoading = loadingRooms || loadingMyBookings;

  return (
    <>
      <LoadingOverlay loading={pageLoading} />
      <div className="p-4 md:p-6 space-y-6 dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-800 dark:text-slate-100">
            Meeting Room Booking
          </h1>
          <p className="text-sm text-gray-500 dark:text-slate-300">
            เลือกห้อง/วัน/เวลา แล้วบันทึกการจอง (ต้อง login ก่อนใช้งาน)
          </p>
        </div>

        <div className="hidden md:flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
          <FiMapPin />
          อาคารสำนักงาน
        </div>
      </div>

      {/* Rooms */}
      <RoomCards
        rooms={rooms}
        selectedRoomId={selectedRoomId}
        onSelectRoom={(id) => {
          setSelectedRoomId(id);
          setEditingBooking(null);
        }}
      />

      <AvailableTimeSlots
        availability={todayAvailability}
        selectedRoomId={selectedRoomId}
        selectedDate={date}
        selectedStartTime={startTime}
        selectedEndTime={endTime}
        onSelectSlot={(slot) => {
          setSelectedRoomId(slot.roomId);
          setDate(todayAvailability.dateKey);
          setStartTime(slot.startTime);
          setEndTime(slot.endTime);
          setEditingBooking(null);
          requestAnimationFrame(() => {
            bookingFormRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          });
        }}
      />



      {/* Booking + Weekly Calendar */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(340px,30%)_minmax(0,1fr)] xl:items-start">
        <div ref={bookingFormRef} className="scroll-mt-24">
          <BookingForm
          selectedRoom={selectedRoom}
          date={date}
          setDate={(d) => {
            setDate(d);
            if (!editingBooking) {
              setEditingBooking(null);
            }
          }}
          startTime={startTime}
          setStartTime={setStartTime}
          endTime={endTime}
          setEndTime={setEndTime}
          purpose={purpose}
          setPurpose={setPurpose}
          onSubmit={onSubmit}
          editingBooking={editingBooking}
          onCancelEdit={resetForm}
          />
        </div>

        <WeeklyBookingCalendar
          selectedRoom={selectedRoom}
          selectedDate={date}
          onSelectDate={(nextDate) => {
            setDate(nextDate);
            setEditingBooking(null);
          }}
          onBookingsChange={setWeeklyBookings}
          onLoadingChange={setLoadingWeeklyBookings}
          refreshKey={calendarRefreshKey}
        />
      </div>

      {/* Daily Schedule */}
      <div className="scroll-mt-24">


        <ScheduleTable
          selectedRoom={selectedRoom}
          date={date}
          myBookings={myBookings}
          onEditBooking={(b) => {
            setEditingBooking(b);
            setStartTime(b.startTime);
            setEndTime(b.endTime);
            setPurpose(b.purpose || "");
            requestAnimationFrame(() => {
              bookingFormRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            });
            toast.info("เข้าสู่โหมดแก้ไข ✏️");
          }}
          onDeleteBooking={async (b) => {
            const confirm = await Swal.fire({
              ...swalBase(),
              icon: "warning",
              title: "ลบรายการจองนี้?",
              html: `
                <div style="text-align:left; line-height:1.6">
                  <div><b>ห้อง:</b> ${selectedRoom?.name || "-"}</div>
                  <div><b>วันที่:</b> ${formatDateDMY(date)}</div>
                  <div><b>เวลา:</b> ${b?.startTime} - ${b?.endTime}</div>
                  <div><b>หัวข้อการประชุม:</b> ${b?.purpose || "-"}</div>
                </div>
              `,
              showCancelButton: true,
              confirmButtonText: "ลบ",
              cancelButtonText: "ยกเลิก",
              confirmButtonColor: "#EF4444",
              reverseButtons: true,
            });

            if (!confirm.isConfirmed) return;

            try {
              const loadingToastId = toast.loading("กำลังลบรายการจอง...");
              await deleteBooking(b._id);

              if (
                editingBooking &&
                String(editingBooking._id) === String(b._id)
              ) {
                resetForm();
              }

              await refreshMyBookings();
              await refreshTodayAvailability();
              setCalendarRefreshKey((current) => current + 1);

              toast.update(loadingToastId, {
                render: "ลบรายการจองสำเร็จ 🗑️",
                type: "success",
                isLoading: false,
                autoClose: 1500,
              });
            } catch (err) {
              console.error(err);
              toast.error(err?.response?.data?.message || "ลบไม่สำเร็จ");
            }
          }}
        />
      </div>

      {showScrollTop && (
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed bottom-5 right-5 z-30 inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-700/70 bg-slate-900/90 text-white shadow-lg shadow-black/30 transition hover:-translate-y-0.5 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:border-slate-600 dark:bg-slate-800/95 dark:hover:bg-slate-700"
          title="กลับขึ้นบนสุด"
          aria-label="กลับขึ้นบนสุด"
        >
          <FiArrowUp className="h-5 w-5" />
        </button>
      )}
      </div>
    </>
  );
};

export default MeetingRooms;
